import { memo, type MouseEvent } from "react";
import { format } from "date-fns";
import {
  Mail,
  Phone,
  MapPin,
  Award,
  Clock,
  Briefcase,
  DollarSign,
  Navigation,
  Stethoscope,
  Wrench
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { StarRating } from "@/components/ui/star-rating";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/utilities/tailwind-utils";
import { ProfileDisplayName } from "@/components/profiles/profile-display-name";
import { formatMoney } from "@/utilities/money-utils";
import { getUserInitials, formatUserFullName } from "@/utilities/name-utils";
import type { Professional } from "@/domain/professionals";
import type { ProfileCardMode } from "@/types/core";

export interface ProfessionalProfileCardProps {
  professional: Professional;
  mode?: ProfileCardMode;
  showAccent?: boolean;
  className?: string;
  onClick?: () => void;
  /** Invoked when the professional name is clicked (e.g. navigate to application). Does not bubble to `onClick`. */
  onNameClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  headerAction?: React.ReactNode;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatAddress(professional: Professional): string {
  if (!professional.address) return "";
  const { street, city, state, postalCode, country } = professional.address;
  return [street, city, state, postalCode, country].filter(Boolean).join(", ");
}

function formatSpecialties(professional: Professional): string {
  if (!professional.specialties?.length) return "";
  return professional.specialties
    .map((s) => s.specialty?.title || "")
    .filter(Boolean)
    .join(", ");
}

function formatRateRange(professional: Professional): string | null {
  const min = professional.hourlyRateMin;
  const max = professional.hourlyRateMax;
  if (!min?.amount && !max?.amount) return null;
  if (min?.amount && max?.amount) {
    return `${formatMoney(min.amount, min.currency)} - ${formatMoney(max.amount, max.currency)}/hr`;
  }
  if (min?.amount) return `From ${formatMoney(min.amount, min.currency)}/hr`;
  if (max?.amount) return `Up to ${formatMoney(max.amount, max.currency)}/hr`;
  return null;
}

// ─── Info Item ───────────────────────────────────────────────────────────────

function InfoItem({
  icon: Icon,
  label,
  value
}: {
  icon: React.ComponentType<{ className?: string }>;
  label?: string;
  value?: string | null;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 min-w-0">
      <div className="flex items-center justify-center size-7 rounded-md bg-muted/60 shrink-0">
        <Icon className="size-3.5 text-muted-foreground" />
      </div>
      <div className="flex flex-col min-w-0 py-0.5">
        {label && (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium leading-none mb-0.5">
            {label}
          </span>
        )}
        <span className="text-sm truncate">{value}</span>
      </div>
    </div>
  );
}

// ─── Compact Mode ────────────────────────────────────────────────────────────

const CompactView = memo(function CompactView({
  professional,
  headerAction,
  onNameClick
}: {
  professional: Professional;
  headerAction?: React.ReactNode;
  onNameClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  const user = professional.user;
  const initials = user ? getUserInitials(user) : "?";
  const fullName = user ? formatUserFullName(user) : "Unknown";
  const specialties = formatSpecialties(professional);
  const rate = formatRateRange(professional);

  return (
    <div className="flex items-start gap-3">
      <Avatar className="size-11 shrink-0 ring-2 ring-primary/10">
        <AvatarImage src={professional.profilePhotoUrl || ""} />
        <AvatarFallback className="bg-primary/8 text-primary font-semibold text-xs">
          {initials}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <ProfileDisplayName compact onClick={onNameClick}>
                {fullName}
              </ProfileDisplayName>
              <StatusBadge status={professional.verificationStatus} />
            </div>
            {specialties && (
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {specialties}
              </p>
            )}
          </div>
          {headerAction}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
          {user?.email && (
            <span className="flex items-center gap-1 truncate max-w-[180px]">
              <Mail className="size-3 shrink-0" />
              <span className="truncate">{user.email}</span>
            </span>
          )}
          {user?.phone && (
            <span className="flex items-center gap-1">
              <Phone className="size-3 shrink-0" />
              {user.phone}
            </span>
          )}
          {rate && (
            <span className="flex items-center gap-1">
              <DollarSign className="size-3 shrink-0" />
              {rate}
            </span>
          )}
        </div>

        {(professional.averageRating > 0 ||
          professional.totalShiftsCompleted > 0 ||
          (professional.yearsOfExperience != null &&
            professional.yearsOfExperience > 0)) && (
            <div className="flex items-center gap-3 mt-1.5">
              {professional.averageRating > 0 && (
                <div className="flex items-center gap-1">
                  <StarRating
                    value={Math.round(professional.averageRating)}
                    size="sm"
                  />
                  <span className="text-xs font-medium text-foreground">
                    {professional.averageRating.toFixed(1)}
                  </span>
                </div>
              )}
              {professional.totalShiftsCompleted > 0 && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Briefcase className="size-3" />
                  {professional.totalShiftsCompleted} shifts
                </span>
              )}
              {professional.yearsOfExperience != null &&
                professional.yearsOfExperience > 0 && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="size-3" />
                    {professional.yearsOfExperience}y exp
                  </span>
                )}
            </div>
          )}
      </div>
    </div>
  );
});

// ─── Extended Mode ───────────────────────────────────────────────────────────

const ExtendedView = memo(function ExtendedView({
  professional,
  headerAction,
  onNameClick
}: {
  professional: Professional;
  headerAction?: React.ReactNode;
  onNameClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  const user = professional.user;
  const initials = user ? getUserInitials(user) : "?";
  const fullName = user ? formatUserFullName(user) : "Unknown";
  const specialties = formatSpecialties(professional);
  const address = formatAddress(professional);
  const rate = formatRateRange(professional);

  return (
    <div className="flex flex-col gap-4">
      {/* Profile Header */}
      <div className="flex items-start gap-4">
        <Avatar className="size-14 shrink-0 ring-2 ring-primary/20 shadow-sm">
          <AvatarImage src={professional.profilePhotoUrl || ""} />
          <AvatarFallback className="bg-primary/8 text-primary font-bold text-base">
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <ProfileDisplayName compact={false} onClick={onNameClick}>
                {fullName}
              </ProfileDisplayName>
              {user?.designation && (
                <p className="text-sm text-muted-foreground truncate mt-0.5">
                  {user.designation}
                </p>
              )}
              {specialties && (
                <p className="text-xs text-muted-foreground/80 truncate mt-0.5">
                  {specialties}
                </p>
              )}
            </div>
            <StatusBadge status={professional.verificationStatus} />
          </div>

          {(professional.averageRating > 0 ||
            professional.totalShiftsCompleted > 0) && (
              <div className="flex items-center gap-4 mt-2">
                {professional.averageRating > 0 && (
                  <div className="flex items-center gap-1.5">
                    <StarRating
                      value={Math.round(professional.averageRating)}
                      size="sm"
                    />
                    <span className="text-sm font-medium">
                      {professional.averageRating.toFixed(1)}
                    </span>
                  </div>
                )}
                {professional.totalShiftsCompleted > 0 && (
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Briefcase className="size-3.5" />
                    {professional.totalShiftsCompleted} shifts completed
                  </span>
                )}
              </div>
            )}
          {headerAction && <div className="mt-2">{headerAction}</div>}
        </div>
      </div>

      <Separator />

      {/* Contact & Details Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <InfoItem icon={Mail} label="Email" value={user?.email} />
        <InfoItem icon={Phone} label="Phone" value={user?.phone} />
        <InfoItem icon={MapPin} label="Address" value={address || undefined} />
        <InfoItem
          icon={Award}
          label="License Number"
          value={professional.licenseNumber}
        />
        <InfoItem
          icon={Clock}
          label="Experience"
          value={
            professional.yearsOfExperience != null
              ? `${professional.yearsOfExperience} years`
              : undefined
          }
        />
        <InfoItem icon={DollarSign} label="Hourly Rate" value={rate} />
        <InfoItem
          icon={Navigation}
          label="Travel Distance"
          value={
            professional.maxTravelDistance
              ? `Up to ${professional.maxTravelDistance} km`
              : undefined
          }
        />
        {professional.createdAt && (
          <InfoItem
            icon={Clock}
            label="Member Since"
            value={format(new Date(professional.createdAt), "MMM d, yyyy")}
          />
        )}
      </div>

      {/* Specialties */}
      {professional.specialties && professional.specialties.length > 0 && (
        <>
          <Separator />
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70 flex items-center gap-1.5">
              <Stethoscope className="size-3" />
              Specialties
            </span>
            <div className="flex flex-wrap gap-1.5">
              {professional.specialties.map((s) => (
                <Badge
                  key={s.specialtyId}
                  variant="secondary"
                  className="text-xs font-medium"
                >
                  {s.specialty?.title}
                  {s.yearsOfExperience != null && s.yearsOfExperience > 0 && (
                    <span className="ml-1 text-muted-foreground font-normal">
                      {s.yearsOfExperience}y
                    </span>
                  )}
                </Badge>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Skills */}
      {professional.skills && professional.skills.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70 flex items-center gap-1.5">
            <Wrench className="size-3" />
            Skills
          </span>
          <div className="flex flex-wrap gap-1.5">
            {professional.skills.map((s) => (
              <Badge
                key={s.skillId}
                variant="outline"
                className="text-xs font-medium"
              >
                {s.skill?.title}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Bio */}
      {professional.bio && (
        <>
          <Separator />
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
              Bio
            </span>
            <div className="rounded-lg bg-muted/40 p-3 border border-border/50">
              <p className="text-sm text-foreground/80 leading-relaxed">
                {professional.bio}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

// ─── Main Component ──────────────────────────────────────────────────────────

export const ProfessionalProfileCard = memo(
  function ProfessionalProfileCard({
    professional,
    mode = "compact",
    showAccent = true,
    className,
    onClick,
    onNameClick,
    headerAction
  }: ProfessionalProfileCardProps) {
    return (
      <Card
        className={cn(
          "overflow-hidden transition-all duration-200",
          mode === "extended" ? "pt-0" : "px-0 pt-0 pb-0",
          onClick &&
          "cursor-pointer hover:shadow-md hover:border-primary/20 active:scale-[0.995]",
          className
        )}
        onClick={onClick}
      >
        {showAccent && mode === "extended" && (
          <div className="h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
        )}
        <CardContent
          className={cn(mode === "extended" ? "pt-0 pb-0" : "py-3 px-3")}
        >
          {mode === "compact" ? (
            <CompactView
              professional={professional}
              headerAction={headerAction}
              onNameClick={onNameClick}
            />
          ) : (
            <ExtendedView
              professional={professional}
              headerAction={headerAction}
              onNameClick={onNameClick}
            />
          )}
        </CardContent>
      </Card>
    );
  }
);

export default ProfessionalProfileCard;
